import type { UseMutationResult } from '@tanstack/react-query'
import type { JSX } from 'react'

import { Button } from '../design-system/button.tsx'
import { Notice } from '../design-system/notice.tsx'
import { ApiRequestError } from '../lib/api-error.ts'
import type {
  AlertChannel,
  AlertPreview,
  AlertRule,
  NewAlert
} from '../lib/alert-rules-api.ts'

/**
 * Where a preview that sent says to look.
 *
 * @param delivery - The channels the preview sent to.
 * @returns The notice's words.
 */
function sentWords(delivery: readonly AlertChannel[]): string {
  const places = [
    ...(delivery.includes('slack')
      ? ['for a DM from BlytzPay Alerts in Slack']
      : []),
    ...(delivery.includes('email') ? ['in your email'] : [])
  ]

  return `Sent. Look ${places.join(', and ')}.`
}

/**
 * Step 4: the alert in the backend's words, and the DM and email it would send now.
 *
 * @param props - Step props.
 * @param props.preview - The last preview, once one has run.
 * @param props.delivery - The channels that preview asked for. The DM shows unless it asked only for email.
 * @returns The step.
 */
export function PreviewStep({
  preview,
  delivery
}: {
  preview: AlertPreview | undefined
  delivery: readonly AlertChannel[]
}): JSX.Element | null {
  if (!preview) {
    return null
  }
  const emailOnly = !delivery.includes('slack')

  return (
    <div data-testid="alert-sheet-preview-result" className="grid gap-2">
      <div data-testid="alert-sheet-words" className="grid gap-1 text-sm">
        <p data-testid="alert-sheet-words-view">{preview.words.view}</p>
        <p data-testid="alert-sheet-words-trigger">{preview.words.trigger}</p>
        <p data-testid="alert-sheet-words-schedule">{preview.words.schedule}</p>
      </div>
      <p data-testid="alert-sheet-reason">{preview.reason}</p>
      {emailOnly ? null : (
        <p
          data-testid="alert-sheet-text"
          className="font-mono text-sm whitespace-pre-wrap"
        >
          {preview.text}
        </p>
      )}
      {preview.email ? (
        <div data-testid="alert-sheet-email" className="grid gap-1">
          <p data-testid="alert-sheet-email-subject" className="font-medium">
            {preview.email.subject}
          </p>
          <p
            data-testid="alert-sheet-email-text"
            className="font-mono text-sm whitespace-pre-wrap"
          >
            {preview.email.text}
          </p>
        </div>
      ) : null}
      {preview.sent && preview.sendErrors.length === 0 ? (
        <Notice data-testid="alert-sheet-sent">{sentWords(delivery)}</Notice>
      ) : null}
      {preview.sent && preview.sendErrors.length > 0 ? (
        <Notice data-testid="alert-sheet-sent-partly" tone="warning">
          {`Sent, but one channel refused: ${preview.sendErrors.join('; ')}`}
        </Notice>
      ) : null}
    </div>
  )
}

/**
 * The last step's three buttons: preview, send the DM now, and save.
 *
 * @param props - Action props.
 * @param props.alert - The alert the draft makes.
 * @param props.preview - The preview request.
 * @param props.save - The save request.
 * @returns The buttons.
 */
export function PreviewActions({
  alert,
  preview,
  save
}: {
  alert: NewAlert
  preview: UseMutationResult<AlertPreview, Error, NewAlert & { send?: boolean }>
  save: UseMutationResult<AlertRule, Error, NewAlert>
}): JSX.Element {
  const busy = preview.isPending || save.isPending

  return (
    <>
      <Button
        data-testid="alert-sheet-preview"
        variant="outline"
        disabled={busy}
        onClick={() => preview.mutate(alert)}
      >
        Preview
      </Button>
      <Button
        data-testid="alert-sheet-send"
        variant="outline"
        disabled={busy}
        onClick={() => preview.mutate({ ...alert, send: true })}
      >
        Send me this now
      </Button>
      <Button
        data-testid="alert-sheet-save"
        disabled={save.isPending}
        onClick={() => save.mutate(alert)}
      >
        Save
      </Button>
    </>
  )
}

/**
 * The API's refusal, including every problem it listed.
 *
 * @param props - Problem props.
 * @param props.error - The failed request.
 * @returns The notice.
 */
export function AlertProblems({ error }: { error: Error }): JSX.Element {
  const details = error instanceof ApiRequestError ? error.details : []

  return (
    <Notice data-testid="alert-sheet-errors" tone="danger">
      {error.message}
      {details.length > 0 ? (
        <ul>
          {details.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
    </Notice>
  )
}
